// Validación de email del signup — NÚCLEO PURO y única fuente de verdad.
//
// Por qué existe este módulo: el estado del email (¿es válido? ¿coincide? ¿el
// dominio recibe? ¿la cuenta existe?) depende de muchas señales, algunas async.
// Cuando esa decisión vive dispersa (gate del submit + handlers de blur + el
// render), cada lugar la re-deriva distinto y se desincronizan → regresiones
// impredecibles ("dice que existe pero no muestra nada", "avanza igual", etc.).
//
// Solución: el estado es una FUNCIÓN PURA de sus entradas. `deriveEmailVerdict`
// devuelve exactamente UN `EmailVerdict` (unión discriminada) para cualquier
// combinación. Todos los consumidores (gate, error de campo, render) salen de
// ese verdict con un `switch` EXHAUSTIVO cerrado por `assertNever`: si alguien
// agrega un estado y olvida manejarlo en algún consumidor, NO COMPILA. La
// cobertura de combinaciones está fijada por el matrix test (emailValidation.test).

import { unknownTld } from './tlds';
import { suggestEmailDomain, type DomainSuggestion } from './emailTypo';

// ---------------------------------------------------------------------------
// Mensajes (colocados con la lógica que los elige, para testearlos juntos)
// ---------------------------------------------------------------------------

export const EMAIL_INVALID_MSG = 'Correo inválido';
export const EMAIL_MISMATCH_MSG = 'Los emails NO coinciden';
export const EMAIL_EXISTS_MSG = 'Ya tenés una cuenta';

export function tldErrorMsg(tld: string): string {
  return `El punto ${tld} NO existe`;
}
export function undeliverableMsg(): string {
  return 'Esa dirección de correo NO existe';
}
export function typoPendingMsg(suggested: string): string {
  return `¿Quisiste poner ${suggested}?`;
}

// ---------------------------------------------------------------------------
// Entradas y verdict
// ---------------------------------------------------------------------------

// Resultado del chequeo de backend (existencia de cuenta + deliverability del
// dominio) para el email actual. 'not-run' = todavía no se consultó (o el
// chequeo falló → fail-open); 'checking' = en vuelo.
export type EmailBackendState = 'not-run' | 'checking' | 'exists' | 'undeliverable' | 'available';

export interface EmailInputs {
  email: string;
  confirm: string;
  /** Email (normalizado) para el que el usuario descartó la sugerencia de typo. */
  typoDismissedFor: string | null;
  /** Resultado del backend PARA EL EMAIL ACTUAL (el caller ya lo asocia por email). */
  backend: EmailBackendState;
}

export type EmailVerdict =
  | { kind: 'empty' } //            campo vacío (todavía no lo llenó)
  | { kind: 'invalid_format' } //   no parece un email
  | { kind: 'bad_tld'; tld: string } // TLD inexistente (lista IANA) — definitivo
  | { kind: 'mismatch'; confirmEmpty: boolean } // email ≠ confirmación
  | { kind: 'typo'; suggestion: DomainSuggestion } // sugerencia sin resolver
  | { kind: 'checking' } //         backend en vuelo
  | { kind: 'exists' } //           ya hay cuenta con ese email
  | { kind: 'undeliverable'; domain: string } // el dominio no recibe correos
  | { kind: 'unverified' } //       cliente OK, backend no consultado aún
  | { kind: 'ok' }; //              todo verde (backend: available)

// Re-export desde el módulo canónico: antes vivía acá como `trim().toLowerCase()`,
// que dejaba pasar espacios internos y Unicode. Se centralizó en
// email-normalize.ts para que la cascada de validación, el input y el submit
// usen exactamente la misma definición de "email normalizado".
import { normalizeEmail } from './email-normalize';

export { normalizeEmail };
function domainOf(normEmail: string): string {
  return normEmail.slice(normEmail.lastIndexOf('@') + 1);
}
// Validación "suficiente" (no RFC 5322): algo@algo.algo. El backend + el mail de
// verificación son el chequeo real de que la casilla existe.
export function isValidEmailFormat(raw: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(raw.trim());
}

/**
 * LA CASCADA. Pura y total: para cualquier combinación de entradas devuelve
 * exactamente un verdict. El orden es intencional (barato→caro y por prioridad
 * de severidad): formato → TLD → coincidencia → typo → backend. La primera
 * objeción que aplica gana. El orden está fijado por el matrix test.
 */
export function deriveEmailVerdict(i: EmailInputs): EmailVerdict {
  const raw = i.email.trim();
  if (raw === '') return { kind: 'empty' };
  if (!isValidEmailFormat(raw)) return { kind: 'invalid_format' };

  const e = normalizeEmail(raw);
  const tld = unknownTld(domainOf(e));
  if (tld) return { kind: 'bad_tld', tld };

  const confirm = normalizeEmail(i.confirm);
  if (e !== confirm) return { kind: 'mismatch', confirmEmpty: confirm === '' };

  if (i.typoDismissedFor !== e) {
    const suggestion = suggestEmailDomain(e);
    if (suggestion) return { kind: 'typo', suggestion };
  }

  switch (i.backend) {
    case 'checking':
      return { kind: 'checking' };
    case 'exists':
      return { kind: 'exists' };
    case 'undeliverable':
      return { kind: 'undeliverable', domain: domainOf(e) };
    case 'available':
      return { kind: 'ok' };
    case 'not-run':
      return { kind: 'unverified' };
    default:
      return assertNever(i.backend);
  }
}

// ---------------------------------------------------------------------------
// Decisión del submit (consumidor #1)
// ---------------------------------------------------------------------------

export type SubmitDecision =
  | { action: 'proceed' } //         email OK, avanzar
  | { action: 'resolve-backend' } // hay que correr el chequeo y re-derivar
  | { action: 'block'; message: string; field: 'email' | 'email_confirm' };

/**
 * ¿Qué hace el submit con este verdict? Exhaustivo: cada kind tiene una acción.
 * 'checking'/'unverified' NO son terminales → el gate resuelve el backend y
 * vuelve a derivar (nunca avanza sin confirmar el dominio/existencia).
 */
export function decideSubmit(v: EmailVerdict): SubmitDecision {
  switch (v.kind) {
    case 'empty':
    case 'invalid_format':
      return { action: 'block', message: EMAIL_INVALID_MSG, field: 'email' };
    case 'bad_tld':
      return { action: 'block', message: tldErrorMsg(v.tld), field: 'email' };
    case 'mismatch':
      return { action: 'block', message: EMAIL_MISMATCH_MSG, field: 'email_confirm' };
    case 'typo':
      return {
        action: 'block',
        message: typoPendingMsg(v.suggestion.suggestedDomain),
        field: 'email',
      };
    case 'exists':
      return { action: 'block', message: EMAIL_EXISTS_MSG, field: 'email' };
    case 'undeliverable':
      return { action: 'block', message: undeliverableMsg(), field: 'email' };
    case 'checking':
    case 'unverified':
      return { action: 'resolve-backend' };
    case 'ok':
      return { action: 'proceed' };
    default:
      return assertNever(v);
  }
}

// ---------------------------------------------------------------------------
// Error de campo (consumidor #2): qué texto va DEBAJO del input de email.
// ---------------------------------------------------------------------------

/**
 * Error a mostrar pegado al campo email (formato/TLD). El resto de los estados
 * (coincidencia, typo, existencia, deliverability) se muestran en el área de
 * estado debajo de la confirmación, no como error del campo. Devuelve null si
 * no hay error de campo.
 */
export function emailFieldError(v: EmailVerdict): string | null {
  switch (v.kind) {
    case 'invalid_format':
      return EMAIL_INVALID_MSG;
    case 'bad_tld':
      return tldErrorMsg(v.tld);
    case 'empty':
    case 'mismatch':
    case 'typo':
    case 'checking':
    case 'exists':
    case 'undeliverable':
    case 'unverified':
    case 'ok':
      return null;
    default:
      return assertNever(v);
  }
}

export function assertNever(x: never): never {
  throw new Error(`unhandled email verdict case: ${JSON.stringify(x)}`);
}
